import React, { useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Box,
    Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useTranslation } from "react-i18next";
import { useStudents } from "../../hooks/useStudents";
import { useStudentAccounts, useCreateStudentAccount, StudentAccountsResponseDto } from "../../hooks/useStudentAccounts";
import { useError } from "../../contexts/ErrorContext";

interface CreateAccountModalProps {
    open: boolean;
    handleClose: () => void;
    classId: number;
    sessionId: number;
}

const CreateAccountModal: React.FC<CreateAccountModalProps> = ({
    open,
    handleClose,
    classId,
    sessionId,
}) => {
    const { t } = useTranslation();
    const { showError, showSuccess } = useError();
    const [searchTerm, setSearchTerm] = useState("");

    const { useStudentsBySessionIdAndClassId } = useStudents();
    const { data: students, isLoading: isLoadingStudents } = useStudentsBySessionIdAndClassId(sessionId, classId);
    const { data: accounts, isLoading: isLoadingAccounts } = useStudentAccounts(classId, sessionId);
    const createAccountMutation = useCreateStudentAccount();

    const studentsWithoutAccounts = useMemo(() => {
        if (!students || !accounts) return [];

        const accountStudentCodes = new Set(accounts.map(acc => acc.student?.studentCode));

        return students.filter(student =>
            !accountStudentCodes.has(student.studentCode)
        );
    }, [students, accounts]);

    const handleCreateAccount = async (student: any) => {
        try {
            await createAccountMutation.mutateAsync(student);
            showSuccess(t("account_created_successfully") || "Student account created successfully");
        } catch (error: any) {
            showError(error?.response?.data || t("error_creating_account") || "Error creating student account");
        }
    };

    const isLoading = isLoadingStudents || isLoadingAccounts;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3 }
            }}
        >
            <DialogTitle sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pb: 1,
                borderBottom: "1px solid",
                borderColor: "divider"
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonAddIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                        {t("create_student_account") || "Create Student Account"}
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : studentsWithoutAccounts.length === 0 ? (
                    <Alert severity="info" sx={{ borderRadius: 2, mt: 1 }}>
                        {t("all_students_have_accounts") || "All students in this class already have accounts created."}
                    </Alert>
                ) : (
                    <TableContainer component={Paper} elevation={0} sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        maxHeight: 400
                    }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: "#f8f9fa" }}>{t("student_code")}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: "#f8f9fa" }}>{t("student_name")}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: "#f8f9fa" }} align="right">{t("action") || "Action"}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {studentsWithoutAccounts.map((student) => (
                                    <TableRow key={student.id} hover>
                                        <TableCell>{student.studentCode}</TableCell>
                                        <TableCell>{student.studentName}</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<PersonAddIcon />}
                                                onClick={() => handleCreateAccount(student)}
                                                disabled={createAccountMutation.isLoading}
                                                sx={{
                                                    textTransform: "none",
                                                    borderRadius: 2,
                                                    boxShadow: "none",
                                                    "&:hover": { boxShadow: "none" }
                                                }}
                                            >
                                                {t("create") || "Create"}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2.5, pt: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
                <Button
                    onClick={handleClose}
                    color="inherit"
                    sx={{ textTransform: "none", borderRadius: 2 }}
                >
                    {t("close")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateAccountModal;
